import React, { useCallback, useMemo, useState } from 'react';
import { useCountryData } from '../hooks/useCountryData';
import { MetricId, metricDomain, metricsForScenario } from '../utils/mapMetrics';
import { useScenario } from '../contexts/ScenarioContext';
import { MAP_RAMPS, rampLegend } from '../utils/color';
import { useTheme } from '../contexts/ThemeContext';
import { formatCompact, formatPct } from '../utils/format';
import { CountryRow } from '../types/census';
import { PageHeader } from '../components/PageHeader';
import { DataBadge } from '../components/DataBadge';
import { SourceRef } from '../components/SourceRef';
import { WorldMap } from '../components/map/WorldMap';
import { CountryPanel } from '../components/map/CountryPanel';
export function MapPage() {
  const data = useCountryData();
  const [metricId, setMetricId] = useState<MetricId>('internet');
  const [selected, setSelected] = useState<CountryRow | null>(null);
  const [rendered, setRendered] = useState<number | null>(null);
  const {
    scenarioId,
    scenario
  } = useScenario();
  const {
    theme
  } = useTheme();
  const mapMetrics = useMemo(() => metricsForScenario(scenarioId), [scenarioId]);
  const metric = mapMetrics.find(m => m.id === metricId) ?? mapMetrics[0];
  const rowsByCcn3 = useMemo(() => {
    const m = new Map<string, CountryRow>();
    data.rows.forEach(r => r.ccn3 && m.set(r.ccn3, r));
    return m;
  }, [data.rows]);
  const domain = useMemo(() => metricDomain(metric, data.rows), [metric, data.rows]);
  const totals = useMemo(() => {
    const pop = data.rows.reduce((s, r) => s + r.population, 0);
    const withNet = data.rows.filter(r => r.internetPct);
    const netPop = withNet.reduce((s, r) => s + r.population, 0);
    const avg = netPop > 0 ? withNet.reduce((s, r) => s + r.population * (r.internetPct?.value ?? 0), 0) / netPop : null;
    return {
      pop,
      avg,
      entries: data.rows.length
    };
  }, [data.rows]);
  const mode = metric.modeKey === 'median' ? 'snapshot' : data.loading ? 'loading' : data.modes[metric.modeKey];
  const asOf = metric.modeKey === 'median' ? 'WPP 2024' : data.asOf[metric.modeKey];
  const onRendered = useCallback((n: number) => setRendered(n), []);
  const close = useCallback(() => setSelected(null), []);
  return <>
      <PageHeader title="World Map" lede="Gall–Peters equal-area projection — every country drawn at its true relative size, not Mercator. Select a country for its full record." data-id="element-674" data-mp="29h7o81yj938p" />

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-675" data-mp="2jbggcojniu6c">
        <dl className="grid grid-cols-3 border-b border-line" data-id="element-676" data-mp="2ob2s0tju02d1">
          <div className="py-5 pr-4" data-id="element-677" data-mp="2t2kl6vfm4yng">
            <dt className="text-[12.5px] text-muted" data-id="element-678" data-mp="2t3m6dsqcf1iw">Countries rendered</dt>
            <dd className="mt-1 font-mono text-xl tabular text-ink md:text-2xl" data-id="element-679" data-mp="2x3mktddfzcpy">
              {rendered ?? '—'}
              <SourceRef id="worldAtlas" data-id="element-680" data-mp="3253il00mqj2b" />
            </dd>
          </div>
          <div className="border-l border-line py-5 pl-4 md:pl-6" data-id="element-681" data-mp="3722dvlw14w23">
            <dt className="text-[12.5px] text-muted" data-id="element-682" data-mp="3h6sjq2uk0kbl">Total population summed</dt>
            <dd className="mt-1 font-mono text-xl tabular text-ink md:text-2xl" data-id="element-683" data-mp="3h4pisnrv56s3">
              {data.loading ? '—' : formatCompact(totals.pop)}
              <SourceRef id="restcountries" data-id="element-684" data-mp="378330dee95wx" />
            </dd>
            <dd className="font-mono text-[11px] text-faint" data-id="element-685" data-mp="37afrzaikkgjh">{totals.entries} entries</dd>
          </div>
          <div className="border-l border-line py-5 pl-4 md:pl-6" data-id="element-686" data-mp="339dqen16jsec">
            <dt className="text-[12.5px] text-muted" data-id="element-687" data-mp="3re5ti0pb8jx">Global internet-user average</dt>
            <dd className="mt-1 font-mono text-xl tabular text-ink md:text-2xl" data-id="element-688" data-mp="3uzi6waltjvaa">
              {totals.avg != null ? formatPct(totals.avg) : '—'}
              <SourceRef id="wbInternet" data-id="element-689" data-mp="3m5ve8tcwr85b" />
            </dd>
            <dd className="font-mono text-[11px] text-faint" data-id="element-690" data-mp="3m5dli6pgfc25">population-weighted</dd>
          </div>
        </dl>

        <div className="flex flex-col gap-4 py-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-691" data-mp="mjorjz384put">
          <div role="radiogroup" aria-label="Color the map by" className="flex flex-wrap gap-1.5" data-id="element-692" data-mp="hqdtamch9hei">
            {mapMetrics.map(m => <button key={m.id} type="button" role="radio" aria-checked={m.id === metricId} onClick={() => setMetricId(m.id)} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] transition-[background-color,border-color,color] duration-150 ease-out ${m.id === metricId ? 'border-machine bg-machine/10 text-ink' : 'border-line text-muted hover:border-faint hover:text-ink'}`} data-id="element-693" data-mp="78h0clmyymaf">
                {m.label}
              </button>)}
          </div>
          <DataBadge mode={mode} source={metric.sourceLabel} asOf={asOf} data-id="element-694" data-mp="g8dgfyvamctv" />
        </div>

        <WorldMap rowsByCcn3={rowsByCcn3} metric={metric} domain={domain} selected={selected?.cca3 ?? null} onSelect={setSelected} onRendered={onRendered} data-id="element-695" data-mp="13yrb7x59ips5" />

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between" data-id="element-696" data-mp="18ru40327tv7a">
          <div className="w-full max-w-md" data-id="element-697" data-mp="2te22s7b2xuo4">
            <div className="flex items-center justify-between font-mono text-[11px] text-muted" data-id="element-698" data-mp="2ohtoew4pi5i0">
              <span data-id="element-699" data-mp="3367s199y5xzm">{data.loading ? '—' : metric.format(domain[0])}</span>
              <span className="text-ink" data-id="element-700" data-mp="2y6v8axedh719">
                {metric.label}
                {metric.id === 'agents' ? ` · ${scenario.label}` : ''}
                {metric.scale === 'log' ? ' · log scale' : ''}
                <SourceRef id={metric.sourceId} estimate={metric.estimate ? {
                low: '−15%',
                high: '+15%'
              } : undefined} data-id="element-701" data-mp="25ifuufaw2ws1" />
              </span>
              <span data-id="element-702" data-mp="2kh1gc67km6nb">{data.loading ? '—' : metric.format(domain[1])}</span>
            </div>
            <div className="mt-1.5 h-2.5 rounded-sm" style={{
            background: rampLegend(MAP_RAMPS[theme])
          }} aria-hidden="true" data-id="element-703" data-mp="2ffkheg8ncu9d" />
          </div>
          <p className="flex items-center gap-2 text-[12px] text-muted" data-id="element-704" data-mp="3vwge3c7lt2jh">
            <span className="h-2.5 w-4 rounded-sm bg-raised ring-1 ring-line" aria-hidden="true" data-id="element-705" data-mp="3qv93z6hznr9n" /> No data · values are also listed in tooltips, the panel and the country table
          </p>
        </div>
        <p className="mt-3 text-[12px] text-muted" data-id="element-706" data-mp="6ql0dv7je7ik">
          India is shown with its official boundaries, including all of Jammu &amp; Kashmir and Ladakh (Aksai Chin) and Arunachal Pradesh.
          <SourceRef id="surveyOfIndia" data-id="element-707" data-mp="gq3is77stl8i" />
          <span className="ml-1" data-id="element-708" data-mp="3f8hfvjl00mgh">Boundaries are simplified to the map’s 1:110m scale.</span>
        </p>
      </section>

      <CountryPanel row={selected} modes={data.modes} onClose={close} data-id="element-709" data-mp="3k6iy62alohqw" />
    </>;
}